import { ComponentBatch } from '../types/componentBatch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const componentBatchApiService = {
  /**
   * Get the next sequential lot number for a component batch
   */
  getNextLotNumber: async (productCode: string, dateOfProduction: string): Promise<string> => {
    const params = new URLSearchParams({ productCode, dateOfProduction });
    const response = await fetch(`${API_BASE_URL}/component-batch/next-lot-number?${params}`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get next lot number');
    }
    return data.lotNumber;
  },

  /**
   * Fetch all component batches from the backend
   */
  fetchComponentBatches: async (): Promise<ComponentBatch[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch component batches');
      }

      return data.componentBatches;
    } catch (error) {
      console.error('Error fetching component batches from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single component batch by ID
   */
  fetchComponentBatchById: async (id: string): Promise<ComponentBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch component batch');
      }

      return data.componentBatch;
    } catch (error) {
      console.error('Error fetching component batch by ID from API:', error);
      throw error;
    }
  },

  /**
   * Create a new component batch
   */
  createComponentBatch: async (batch: ComponentBatch): Promise<ComponentBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create component batch');
      }

      return data.componentBatch;
    } catch (error) {
      console.error('Error creating component batch:', error);
      throw error;
    }
  },

  /**
   * Update an existing component batch
   */
  updateComponentBatch: async (batch: ComponentBatch): Promise<ComponentBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch/${batch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update component batch');
      }

      return data.componentBatch;
    } catch (error) {
      console.error('Error updating component batch:', error);
      throw error;
    }
  },

  /**
   * Delete a component batch by ID
   */
  deleteComponentBatch: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/component-batch/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete component batch');
    }
  }
};
